import { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { selectCustomerIsInsuranceAgent, selectIsSoloRea } from '../store/selectors/customerProfile'
import { selectPrequalRequestFailure, selectPrequalRequestSuccess } from '../store/selectors/affordability'

import { useDispatch, useSelector } from 'react-redux'
import { sendPrequalRequestForClient } from '../actions'
import { useTrackingContext } from '../providers/tracking'
import { Listing, MessageTopicKey } from '../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from './listings/useSendListingMessage'
import * as Sentry from '@sentry/browser'

interface SendPrequalParams {
  topic: string
  topicKey: string
  message: string
  metadata?: Record<string, any>
}

export const usePrequalRequest = () => {
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const isSoloRea = useSelector(selectIsSoloRea)
  const isInsuranceAgent = useSelector(selectCustomerIsInsuranceAgent)
  const prequalRequestSuccess = useSelector(selectPrequalRequestSuccess)
  const prequalRequestFailure = useSelector(selectPrequalRequestFailure)
  const recentlySubmitted = sessionStorage.getItem('prequalRequestSent') === 'true'
  const [isLoading, setIsLoading] = useState(false)
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  // Track component mounting to prevent state updates after unmount
  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const canSendPrequal = useMemo(
    () => !isSoloRea && !isInsuranceAgent && !recentlySubmitted,
    [isSoloRea, isInsuranceAgent, recentlySubmitted]
  )

  const persistPrequalRequestSent = useCallback(() => {
    sessionStorage.setItem('prequalRequestSent', 'true')
  }, [])

  const sendPrequal = useCallback(
    async ({ topic, topicKey, message, metadata }: SendPrequalParams): Promise<void> => {
      if (!isMountedRef.current) return

      setIsLoading(true)

      try {
        await dispatch(
          sendPrequalRequestForClient(trackingClient, {
            uiContext: topic,
            customMessage: message,
            customTopic: topic,
            customTopicKey: topicKey,
            eventSource: metadata?.event_source
          })
        )

        // Only persist if the component is still mounted
        if (isMountedRef.current) {
          persistPrequalRequestSent()
        }
      } catch (error) {
        Sentry.captureException(error)
        throw error
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [dispatch, trackingClient, persistPrequalRequestSent]
  )

  const sendPrequalForListing = useCallback(
    async ({
      listing,
      topic = 'Listing Pre-qualification',
      topicKey = MessageTopicKey.ListingsPrequalRequest,
      message = 'I want to get pre-qualified for this home.'
    }: {
      listing: Listing
      topic?: 'Listing Pre-qualification'
      topicKey?: MessageTopicKey
      message?: string
    }): Promise<void> => {
      if (!isMountedRef.current) return

      setIsLoading(true)

      try {
        await sendListingMessage({
          input: {
            message,
            topic,
            topicKey,
            listingId: listing.id
          }
        })

        if (isMountedRef.current) {
          persistPrequalRequestSent()
        }
      } catch (error) {
        Sentry.captureException(error)
        throw error
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [sendListingMessage, persistPrequalRequestSent]
  )

  return {
    canSendPrequal,
    sendPrequal,
    sendPrequalForListing,
    isLoading,
    isError: prequalRequestFailure,
    isSuccess: prequalRequestSuccess
  }
}
